import { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { budgetsApi, settingsApi, forecastsApi } from '../services/api';
import { generateStandardName, getBuCode, sortAlphabetically, sortFiscalYearsNewestFirst } from '../lib/namingUtils';
import { CreateBudgetDialog } from '../components/budgets';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import CommentsSidePanel from '../components/CommentsSidePanel';
import ImportActualsModal from '../components/ImportActualsModal';
import { 
  Calculator,
  Trash2,
  Calendar as CalendarIcon,
  Building2,
  CheckCircle2,
  Clock,
  XCircle,
  Eye,
  ArrowLeft,
  FileText,
  Send,
  Filter,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Layers,
  History,
  Plus,
  Edit,
  MessageSquare,
  LayoutGrid,
  Upload,
  List,
  Grid,
  TrendingUp
} from 'lucide-react';
import { Input } from '../components/ui/input';

const BUDGET_STATUS = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', darkColor: 'bg-zinc-700 text-zinc-300', icon: FileText },
  pending: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', darkColor: 'bg-amber-900/30 text-amber-400', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', darkColor: 'bg-emerald-900/30 text-emerald-400', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', darkColor: 'bg-red-900/30 text-red-400', icon: XCircle },
};

const MONTHS = ['jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'jan', 'feb', 'mar', 'apr', 'may', 'jun'];
const MONTH_NAMES = {
  jul: 'July', aug: 'August', sep: 'September', oct: 'October', nov: 'November', dec: 'December',
  jan: 'January', feb: 'February', mar: 'March', apr: 'April', may: 'May', jun: 'June'
};

const CATEGORY_COLORS = {
  'Sales': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
  'Purchases': { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  'Overhead': { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
  'Salaries': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  'Management Fees': { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
  'Other Expenses': { bg: 'bg-slate-50 dark:bg-zinc-800/50', text: 'text-slate-700 dark:text-zinc-400', border: 'border-slate-200 dark:border-zinc-700' },
};

const TIME_PERIODS = [
  { value: 'all', label: 'Full Year' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'q1', label: 'Q1 (Jul-Sep)' },
  { value: 'q2', label: 'Q2 (Oct-Dec)' },
  { value: 'q3', label: 'Q3 (Jan-Mar)' },
  { value: 'q4', label: 'Q4 (Apr-Jun)' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);
  return value < 0 ? `(${formatted})` : formatted;
};

// Format currency as expense - always shows in parentheses using absolute value
const formatCurrencyAsExpense = (value) => {
  if (value === null || value === undefined) return '-';
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);
  return `(${formatted})`;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const BudgetsPage = () => {
  const { isDark, setSelectedBu: setThemeBu } = useTheme();
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [budgets, setBudgets] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  
  // Filters
  const [selectedBu, setSelectedBuLocal] = useState('');
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timePeriod, setTimePeriod] = useState('all');
  
  // Wrapper to update both local state and theme context
  const setSelectedBu = (buId) => {
    setSelectedBuLocal(buId);
    const bu = businessUnits.find(b => b.id === buId);
    if (setThemeBu) {
      setThemeBu(buId || '', bu?.logo_url);
    }
  };
  
  // View states
  const [viewingBudget, setViewingBudget] = useState(null);
  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState({ open: false, action: null, budget: null });
  const [revisionDialog, setRevisionDialog] = useState({ open: false, budget: null });
  
  // Previous budget selection
  const [selectedPreviousBudget, setSelectedPreviousBudget] = useState(null);
  const [availablePreviousBudgets, setAvailablePreviousBudgets] = useState([]);
  
  // Create budget dialog
  const [createBudgetDialog, setCreateBudgetDialog] = useState(false);
  const [newBudgetName, setNewBudgetName] = useState('');
  const [newBudgetBuId, setNewBudgetBuId] = useState('');
  const [newBudgetFiscalYear, setNewBudgetFiscalYear] = useState('');
  const [creatingBudget, setCreatingBudget] = useState(false);
  const [budgetSource, setBudgetSource] = useState('scratch'); // 'scratch' or 'forecast'
  const [selectedForecastId, setSelectedForecastId] = useState('');
  const [availableForecasts, setAvailableForecasts] = useState([]);
  
  // Consolidated view state
  const [showConsolidated, setShowConsolidated] = useState(false);
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [loadingConsolidated, setLoadingConsolidated] = useState(false);
  const [consolidatedHideZeros, setConsolidatedHideZeros] = useState(false);
  const [consolidatedExpandedCategories, setConsolidatedExpandedCategories] = useState(new Set(['Sales', 'Purchases']));
  
  // Import Actuals modal state
  const [importActualsModal, setImportActualsModal] = useState({ open: false, budget: null });
  
  // List/Tile view toggle (default to list)
  const [viewStyle, setViewStyle] = useState('list');
  
  // Financial Summary state
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Sort business units alphabetically and fiscal years newest first
  const sortedBusinessUnits = useMemo(() => sortAlphabetically(businessUnits, 'name'), [businessUnits]);
  const sortedFiscalYears = useMemo(() => sortFiscalYearsNewestFirst(fiscalYears), [fiscalYears]);

  // Check if user has BU restriction - use hasPermission from useAuth
  const hasPermission = (perm) => isAdmin() || user?.permissions?.includes(perm);
  const userBuId = user?.business_unit_id;
  const canSeeAllBUs = isAdmin() || user?.is_cfo || !userBuId;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadBudgets();
  }, [selectedBu, selectedFiscalYear, statusFilter]);

  // Load financial summary when filters change
  useEffect(() => {
    loadFinancialSummary();
  }, [selectedBu, selectedFiscalYear]);

  const loadFinancialSummary = async () => {
    try {
      setLoadingSummary(true);
      const params = { status: 'approved' };
      if (selectedFiscalYear) params.fiscal_year = selectedFiscalYear;
      if (selectedBu) params.business_unit_id = selectedBu;
      
      const res = await budgetsApi.getFinancialSummary(params);
      setFinancialSummary(res.data);
    } catch (error) {
      console.error('Failed to load financial summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  // Auto-generate budget name when BU or FY changes
  useEffect(() => {
    if (newBudgetBuId && newBudgetFiscalYear) {
      const selectedBuObj = businessUnits.find(bu => bu.id === newBudgetBuId);
      const buCode = getBuCode(selectedBuObj);
      const generatedName = generateStandardName(buCode, newBudgetFiscalYear, 'Budget');
      setNewBudgetName(generatedName);
    }
  }, [newBudgetBuId, newBudgetFiscalYear, businessUnits]);

  // Load available forecasts when BU changes in create dialog
  useEffect(() => {
    const loadForecasts = async () => {
      if (newBudgetBuId) {
        try {
          const res = await forecastsApi.getAll({ business_unit_id: newBudgetBuId });
          // Only show active/archived forecasts
          const activeForecasts = res.data.filter(f => f.status === 'active' || f.status === 'archived');
          setAvailableForecasts(activeForecasts);
        } catch (error) {
          console.error('Failed to load forecasts:', error);
          setAvailableForecasts([]);
        }
      } else {
        setAvailableForecasts([]);
      }
    };
    loadForecasts();
  }, [newBudgetBuId]);

  const loadData = async () => {
    try {
      const [busRes, fyRes] = await Promise.all([
        settingsApi.getBusinessUnits(),
        budgetsApi.getFiscalYears()
      ]);
      setBusinessUnits(busRes.data);
      setFiscalYears(fyRes.data.length > 0 ? fyRes.data : ['2024/2025', '2025/2026']);
      
      // Determine initial filter values
      const initialFiscalYear = fyRes.data.length > 0 ? fyRes.data[0] : '';
      const initialBu = (userBuId && !canSeeAllBUs) ? userBuId : '';
      
      // Set state values
      if (initialFiscalYear) {
        setSelectedFiscalYear(initialFiscalYear);
      }
      if (initialBu) {
        setSelectedBu(initialBu);
      }
      
      // Load budgets with explicit filter values (don't rely on state which is async)
      await loadBudgetsWithParams(initialBu, initialFiscalYear);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  // Load budgets with explicit parameters (used during initial load)
  const loadBudgetsWithParams = async (buId, fiscalYear) => {
    try {
      setRefreshing(true);
      const params = {};
      
      if (!canSeeAllBUs && userBuId) {
        params.business_unit_id = userBuId;
      } else if (buId) {
        params.business_unit_id = buId;
      }
      
      if (fiscalYear) params.fiscal_year = fiscalYear;
      
      const res = await budgetsApi.getAll(params);
      setBudgets(res.data);
    } catch (error) {
      console.error('Failed to load budgets:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const loadBudgets = async (clearFilters = false) => {
    try {
      setRefreshing(true);
      const params = {};
      
      // If clearFilters is true, don't apply any filters for this load
      if (!clearFilters) {
        // If user has BU restriction and not admin/CFO, always filter by their BU
        if (!canSeeAllBUs && userBuId) {
          params.business_unit_id = userBuId;
        } else if (selectedBu && selectedBu !== 'all') {
          params.business_unit_id = selectedBu;
        }
        
        if (selectedFiscalYear) params.fiscal_year = selectedFiscalYear;
        if (statusFilter !== 'all') params.status = statusFilter;
      }
      
      const res = await budgetsApi.getAll(params);
      setBudgets(res.data);
    } catch (error) {
      console.error('Failed to load budgets:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Load consolidated data when toggle is on
  const loadConsolidatedData = async () => {
    if (!selectedFiscalYear) {
      toast.error('Please select a fiscal year first');
      return;
    }
    
    try {
      setLoadingConsolidated(true);
      const params = { fiscal_year: selectedFiscalYear };
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const res = await budgetsApi.getConsolidatedBudgets(params);
      setConsolidatedData(res.data);
    } catch (error) {
      console.error('Failed to load consolidated data:', error);
      toast.error('Failed to load consolidated data');
    } finally {
      setLoadingConsolidated(false);
    }
  };

  // Load consolidated data when toggle changes or fiscal year changes
  useEffect(() => {
    if (showConsolidated && selectedFiscalYear) {
      loadConsolidatedData();
    }
  }, [showConsolidated, selectedFiscalYear, statusFilter]);

  const openBudgetDetail = async (budget) => {
    try {
      const res = await budgetsApi.getOne(budget.id);
      setViewingBudget(res.data);
      setDetailViewOpen(true);
      setSelectedPreviousBudget(null);
      
      // Load available previous budgets for the same BU
      if (budget.business_unit_id) {
        const allBudgets = await budgetsApi.getAll({ business_unit_id: budget.business_unit_id });
        const previous = allBudgets.data.filter(b => 
          b.id !== budget.id && 
          (b.fiscal_year < budget.fiscal_year || (b.fiscal_year === budget.fiscal_year && b.created_at < budget.created_at))
        ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setAvailablePreviousBudgets(previous);
      }
    } catch (error) {
      toast.error('Failed to load budget details');
    }
  };

  const loadPreviousBudget = async (budgetId) => {
    if (!budgetId) {
      setSelectedPreviousBudget(null);
      return;
    }
    try {
      const res = await budgetsApi.getOne(budgetId);
      setSelectedPreviousBudget(res.data);
    } catch (error) {
      toast.error('Failed to load previous budget');
    }
  };

  const handleApprovalAction = async (comment) => {
    const { action, budget } = approvalDialog;
    try {
      await budgetsApi.updateStatus(budget.id, action === 'approve' ? 'approved' : 'rejected', comment);
      toast.success(`Budget ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      await loadBudgets();
      if (viewingBudget?.id === budget.id) {
        const res = await budgetsApi.getOne(budget.id);
        setViewingBudget(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update budget status');
    }
    setApprovalDialog({ open: false, action: null, budget: null });
  };

  const handleSubmitForApproval = async (budget) => {
    try {
      await budgetsApi.updateStatus(budget.id, 'pending');
      toast.success('Budget submitted for approval');
      await loadBudgets();
      if (viewingBudget?.id === budget.id) {
        const res = await budgetsApi.getOne(budget.id);
        setViewingBudget(res.data);
      }
    } catch (error) {
      toast.error('Failed to submit budget');
    }
  };

  const handleRevision = async (note) => {
    try {
      await budgetsApi.revise(revisionDialog.budget.id, note);
      toast.success('Budget marked for revision');
      await loadBudgets();
      if (viewingBudget?.id === revisionDialog.budget.id) {
        const res = await budgetsApi.getOne(revisionDialog.budget.id);
        setViewingBudget(res.data);
      }
    } catch (error) {
      toast.error('Failed to revise budget');
    }
    setRevisionDialog({ open: false, budget: null });
  };

  const handleDeleteBudget = async (id) => {
    if (!window.confirm('Are you sure you want to delete this budget?')) return;
    try {
      // Immediately remove from UI for instant feedback
      setBudgets(prev => prev.filter(b => b.id !== id));
      
      // If viewing this budget, close the detail view immediately
      if (viewingBudget?.id === id) {
        setDetailViewOpen(false);
        setViewingBudget(null);
      }
      
      await budgetsApi.delete(id);
      toast.success('Budget deleted');
      
      // Refresh to sync with server - first try with current filters
      const res = await budgetsApi.getAll({
        ...(selectedFiscalYear && { fiscal_year: selectedFiscalYear }),
        ...(selectedBu && selectedBu !== 'all' && { business_unit_id: selectedBu }),
        ...(statusFilter !== 'all' && { status: statusFilter })
      });
      
      // If result is empty but we have filters, clear filters and reload
      if (res.data.length === 0 && (selectedFiscalYear || (selectedBu && selectedBu !== 'all'))) {
        setSelectedFiscalYear('');
        setSelectedBu('all');
        setStatusFilter('all');
        toast.info('Filters cleared to show remaining budgets');
        await loadBudgets(true); // Load without filters
      } else {
        setBudgets(res.data);
      }
    } catch (error) {
      toast.error('Failed to delete budget');
      // Reload on error to restore state
      await loadBudgets();
    }
  };

  const handleCreateBudget = async () => {
    if (!newBudgetName.trim()) {
      toast.error('Please enter a budget name');
      return;
    }
    if (!newBudgetBuId) {
      toast.error('Please select a Business Unit');
      return;
    }
    if (!newBudgetFiscalYear) {
      toast.error('Please select a Fiscal Year');
      return;
    }
    if (budgetSource === 'forecast' && !selectedForecastId) {
      toast.error('Please select a forecast to copy from');
      return;
    }

    setCreatingBudget(true);
    try {
      const bu = businessUnits.find(b => b.id === newBudgetBuId);
      const response = await budgetsApi.importFromFile({
        name: newBudgetName,
        business_unit_id: newBudgetBuId,
        business_unit_name: bu?.name || '',
        fiscal_year: newBudgetFiscalYear,
        source_forecast_id: budgetSource === 'forecast' ? selectedForecastId : null
      });
      
      const sourceMsg = budgetSource === 'forecast' ? ' from forecast' : '';
      toast.success(`Budget "${newBudgetName}" created${sourceMsg}! You can now edit the figures.`);
      setCreateBudgetDialog(false);
      setNewBudgetName('');
      setNewBudgetBuId('');
      setNewBudgetFiscalYear('');
      setBudgetSource('scratch');
      setSelectedForecastId('');
      await loadBudgets();
      
      // Open the newly created budget for editing
      if (response.data?.id) {
        const newBudget = await budgetsApi.getOne(response.data.id);
        setViewingBudget(newBudget.data);
        setDetailViewOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create budget');
    } finally {
      setCreatingBudget(false);
    }
  };

  // Generate fiscal year options for create dialog
  const fiscalYearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear - 2; i <= currentYear + 2; i++) {
    fiscalYearOptions.push(`${i}/${i + 1}`);
  }

  // Get months to display based on time period
  const getDisplayMonths = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const fiscalMonthMap = { 6: 0, 7: 1, 8: 2, 9: 3, 10: 4, 11: 5, 0: 6, 1: 7, 2: 8, 3: 9, 4: 10, 5: 11 };
    const currentFiscalMonth = fiscalMonthMap[currentMonth];
    
    switch (timePeriod) {
      case 'all': return MONTHS;
      case 'ytd': return MONTHS.slice(0, currentFiscalMonth + 1);
      case 'q1': return ['jul', 'aug', 'sep'];
      case 'q2': return ['oct', 'nov', 'dec'];
      case 'q3': return ['jan', 'feb', 'mar'];
      case 'q4': return ['apr', 'may', 'jun'];
      case 'this_quarter':
        const quarterStart = Math.floor(currentFiscalMonth / 3) * 3;
        return MONTHS.slice(quarterStart, quarterStart + 3);
      case 'last_quarter':
        const lastQuarterStart = (Math.floor(currentFiscalMonth / 3) - 1) * 3;
        if (lastQuarterStart < 0) return ['apr', 'may', 'jun'];
        return MONTHS.slice(lastQuarterStart, lastQuarterStart + 3);
      case 'this_month': return [MONTHS[currentFiscalMonth]];
      case 'last_month':
        const lastMonth = currentFiscalMonth > 0 ? currentFiscalMonth - 1 : 11;
        return [MONTHS[lastMonth]];
      default: return MONTHS;
    }
  };

  const displayMonths = getDisplayMonths();

  const stats = useMemo(() => ({
    total: budgets.length,
    approved: budgets.filter(b => b.status === 'approved').length,
    pending: budgets.filter(b => b.status === 'pending').length,
    draft: budgets.filter(b => b.status === 'draft').length,
    rejected: budgets.filter(b => b.status === 'rejected').length,
  }), [budgets]);

  const canApprove = hasPermission('approve') || isAdmin();

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="budgets-loading">
        <Skeleton className="h-10 w-64 rounded-sm" />
        <div className="grid grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}
        </div>
        <Skeleton className="h-96 rounded-sm" />
      </div>
    );
  }

  // Budget Detail View
  if (detailViewOpen && viewingBudget) {
    const handleSaveLineItems = async (lineItems) => {
      await budgetsApi.updateLineItems(viewingBudget.id, lineItems);
      // Reload the budget to get updated data
      const res = await budgetsApi.getOne(viewingBudget.id);
      setViewingBudget(res.data);
      await loadBudgets();
    };

    return (
      <BudgetDetailView
        budget={selectedPreviousBudget || viewingBudget}
        currentBudget={viewingBudget}
        displayMonths={displayMonths}
        timePeriod={timePeriod}
        setTimePeriod={setTimePeriod}
        canApprove={canApprove}
        hasWritePermission={hasPermission('write')}
        isViewingPrevious={!!selectedPreviousBudget}
        availablePreviousBudgets={availablePreviousBudgets}
        selectedPreviousBudgetId={selectedPreviousBudget?.id}
        onSelectPreviousBudget={loadPreviousBudget}
        onBack={() => { setDetailViewOpen(false); setViewingBudget(null); setSelectedPreviousBudget(null); }}
        onApprove={() => setApprovalDialog({ open: true, action: 'approve', budget: viewingBudget })}
        onReject={() => setApprovalDialog({ open: true, action: 'reject', budget: viewingBudget })}
        onSubmitForApproval={() => handleSubmitForApproval(viewingBudget)}
        onRevise={() => setRevisionDialog({ open: true, budget: viewingBudget })}
        onSaveChanges={handleSaveLineItems}
        isDark={isDark}
      />
    );
  }

  // Main Budgets List View
  return (
    <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="budgets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`font-display text-3xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Budgets
          </h1>
          <p className={isDark ? 'text-zinc-400 mt-1' : 'text-slate-500 mt-1'}>
            View and manage organizational budgets by business unit
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select 
            value={selectedBu || 'all'} 
            onValueChange={(v) => setSelectedBu(v === 'all' ? '' : v)}
            disabled={!canSeeAllBUs}
          >
            <SelectTrigger className={`w-[180px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Business Unit" />
            </SelectTrigger>
            <SelectContent>
              {canSeeAllBUs && <SelectItem value="all">All Business Units</SelectItem>}
              {sortedBusinessUnits.map(bu => (
                <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedFiscalYear || 'all'} onValueChange={(v) => setSelectedFiscalYear(v === 'all' ? '' : v)}>
            <SelectTrigger className={`w-[150px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
              <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Fiscal Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {sortedFiscalYears.map(fy => (
                <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className={`w-[160px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map(period => (
                <SelectItem key={period.value} value={period.value}>{period.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={() => loadBudgets()} disabled={refreshing} className="rounded-sm">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>

          {/* List/Tile View Toggle */}
          <div className={`flex items-center rounded-sm overflow-hidden border ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => setViewStyle('list')}
              className={`rounded-none px-3 h-9 ${viewStyle === 'list' 
                ? isDark ? 'bg-zinc-700 text-white' : 'bg-slate-200 text-slate-900'
                : isDark ? 'text-zinc-400' : 'text-slate-500'
              }`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => setViewStyle('tile')}
              className={`rounded-none px-3 h-9 border-l ${isDark ? 'border-zinc-700' : 'border-slate-200'} ${viewStyle === 'tile' 
                ? isDark ? 'bg-zinc-700 text-white' : 'bg-slate-200 text-slate-900'
                : isDark ? 'text-zinc-400' : 'text-slate-500'
              }`}
              data-testid="view-tile-btn"
            >
              <Grid className="w-4 h-4" />
            </Button>
          </div>

          {/* Consolidated View Toggle - Only for Admin/CFO */}
          {canSeeAllBUs && (
            <Button 
              variant={showConsolidated ? "default" : "outline"}
              onClick={() => {
                if (!selectedFiscalYear && !showConsolidated) {
                  toast.error('Please select a Fiscal Year first for consolidated view');
                  return;
                }
                setShowConsolidated(!showConsolidated);
              }} 
              className={`rounded-sm ${showConsolidated ? 'bg-purple-600 hover:bg-purple-700 text-white' : ''}`}
              data-testid="consolidated-view-toggle"
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              {showConsolidated ? 'Exit Consolidated' : 'Consolidated View'}
            </Button>
          )}

          {hasPermission('write') && !showConsolidated && (
            <Button 
              onClick={() => setCreateBudgetDialog(true)} 
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              data-testid="create-budget-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Budget
            </Button>
          )}
        </div>
      </div>

      {/* Status Filter Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { key: 'all', label: 'Total Budgets', count: stats.total, color: '' },
          { key: 'approved', label: 'Approved', count: stats.approved, color: 'emerald' },
          { key: 'pending', label: 'Pending', count: stats.pending, color: 'amber' },
          { key: 'draft', label: 'Drafts', count: stats.draft, color: 'slate' },
          { key: 'rejected', label: 'Rejected', count: stats.rejected, color: 'red' },
        ].map(item => (
          <Card 
            key={item.key}
            className={`border shadow-none rounded-sm cursor-pointer transition-all ${
              statusFilter === item.key 
                ? `ring-2 ring-emerald-600 ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`
                : isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setStatusFilter(item.key)}
            data-testid={`filter-${item.key}`}
          >
            <CardContent className="p-4">
              <p className={isDark ? 'text-zinc-400 text-sm' : 'text-slate-500 text-sm'}>{item.label}</p>
              <p className={`font-mono text-2xl font-medium mt-1 ${
                item.color === 'emerald' ? 'text-emerald-600' :
                item.color === 'amber' ? 'text-amber-600' :
                item.color === 'red' ? 'text-red-600' :
                isDark ? 'text-white' : 'text-slate-900'
              }`}>
                {item.count}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Active Filters */}
      {(statusFilter !== 'all' || selectedBu || timePeriod !== 'all') && (
        <div className={`flex items-center gap-2 flex-wrap ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
          <span className="text-sm">Active filters:</span>
          {statusFilter !== 'all' && (
            <Badge className={isDark ? BUDGET_STATUS[statusFilter]?.darkColor : BUDGET_STATUS[statusFilter]?.color}>
              {BUDGET_STATUS[statusFilter]?.label}
            </Badge>
          )}
          {selectedBu && (
            <Badge variant="outline">{businessUnits.find(b => b.id === selectedBu)?.name}</Badge>
          )}
          {timePeriod !== 'all' && (
            <Badge variant="outline">{TIME_PERIODS.find(p => p.value === timePeriod)?.label}</Badge>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setStatusFilter('all'); setSelectedBu(''); setTimePeriod('all'); }}
            className="text-xs h-6"
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Consolidated View or Budgets List */}
      {showConsolidated ? (
        /* CONSOLIDATED VIEW - Matching Budget Template Structure */
        <div className="space-y-6">
          {/* Consolidated Header with Controls */}
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="font-display text-lg font-medium flex items-center gap-2">
                    <LayoutGrid className="w-5 h-5 text-purple-500" />
                    Consolidated Budget View
                  </CardTitle>
                  {consolidatedData && (
                    <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                      {consolidatedData.budget_count} budget(s) from: {consolidatedData.business_units_included?.map(bu => bu.name).join(', ')}
                    </p>
                  )}
                </div>
                
                {/* Controls */}
                <div className="flex items-center gap-3">
                  {/* Fiscal Year Selector */}
                  <Select value={selectedFiscalYear} onValueChange={(fy) => {
                    setSelectedFiscalYear(fy);
                  }}>
                    <SelectTrigger className={`w-40 rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white'}`} data-testid="consolidated-fy-select">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Fiscal Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedFiscalYears.map(fy => (
                        <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Hide Zeros Toggle */}
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-slate-200'}`}>
                    <input 
                      type="checkbox"
                      id="hide-zeros-budget-consolidated" 
                      checked={consolidatedHideZeros} 
                      onChange={(e) => setConsolidatedHideZeros(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="hide-zeros-budget-consolidated" className={`text-sm cursor-pointer ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                      Hide Zeros
                    </label>
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          {loadingConsolidated ? (
            <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <CardContent className="p-8 text-center">
                <RefreshCw className={`w-8 h-8 mx-auto mb-4 animate-spin ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
                <p className={isDark ? 'text-zinc-400' : 'text-slate-500'}>Loading consolidated data...</p>
              </CardContent>
            </Card>
          ) : consolidatedData && consolidatedData.line_items?.length > 0 ? (
            <>
              {/* Summary Cards */}
              {(() => {
                // Calculate totals from consolidated data
                const totals = {
                  sales: { budget: 0 },
                  purchases: { budget: 0 },
                  overhead: { budget: 0 },
                  salaries: { budget: 0 },
                  mgmtFees: { budget: 0 },
                  otherExpenses: { budget: 0 },
                };
                
                for (const item of consolidatedData.line_items) {
                  const rowTotal = MONTHS.reduce((sum, m) => sum + (item[m] || 0), 0);
                  const category = item.category || 'Other Expenses';
                  if (category === 'Sales') totals.sales.budget += rowTotal;
                  else if (category === 'Purchases') totals.purchases.budget += rowTotal;
                  else if (category === 'Overhead') totals.overhead.budget += rowTotal;
                  else if (category === 'Salaries') totals.salaries.budget += rowTotal;
                  else if (category === 'Management Fees') totals.mgmtFees.budget += rowTotal;
                  else totals.otherExpenses.budget += rowTotal;
                }
                
                const grossProfit = totals.sales.budget - totals.purchases.budget;
                const opex = totals.overhead.budget + totals.salaries.budget + totals.mgmtFees.budget + totals.otherExpenses.budget;
                // OPEX is already negative (sum of negative expenses), so we ADD it
                const operatingProfit = grossProfit + opex;
                const netProfit = operatingProfit;
                
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                      <CardContent className="p-3">
                        <p className={`text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Sales</p>
                        <p className={`font-mono text-lg font-medium mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatCurrency(totals.sales.budget)}</p>
                      </CardContent>
                    </Card>
                    <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                      <CardContent className="p-3">
                        <p className={`text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Purchases</p>
                        <p className={`font-mono text-lg font-medium mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatCurrency(totals.purchases.budget)}</p>
                      </CardContent>
                    </Card>
                    <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-emerald-900/20 border-emerald-800' : 'bg-emerald-50 border-emerald-200'}`}>
                      <CardContent className="p-3">
                        <p className={`text-xs font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Gross Profit</p>
                        <p className={`font-mono text-lg font-medium mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>{formatCurrency(grossProfit)}</p>
                      </CardContent>
                    </Card>
                    <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                      <CardContent className="p-3">
                        <p className={`text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Opex</p>
                        <p className={`font-mono text-lg font-medium mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatCurrency(opex)}</p>
                      </CardContent>
                    </Card>
                    <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                      <CardContent className="p-3">
                        <p className={`text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Operating Profit</p>
                        <p className={`font-mono text-lg font-medium mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatCurrency(operatingProfit)}</p>
                      </CardContent>
                    </Card>
                    <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-emerald-900/20 border-emerald-800' : 'bg-emerald-50 border-emerald-200'}`}>
                      <CardContent className="p-3">
                        <p className={`text-xs font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Net Profit</p>
                        <p className={`font-mono text-lg font-medium mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>{formatCurrency(netProfit)}</p>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}

              {/* Category Tables - Collapsible */}
              <div className="space-y-4">
                {(() => {
                  // Group items by category
                  const grouped = {};
                  for (const item of consolidatedData.line_items) {
                    const category = item.category || 'Other Expenses';
                    if (!grouped[category]) grouped[category] = [];
                    
                    const rowTotal = MONTHS.reduce((sum, m) => sum + (item[m] || 0), 0);
                    
                    // Skip zero items if hide zeros is enabled
                    if (consolidatedHideZeros && rowTotal === 0) continue;
                    
                    grouped[category].push({ ...item, rowTotal });
                  }
                  
                  const toggleCategory = (cat) => {
                    setConsolidatedExpandedCategories(prev => {
                      const next = new Set(prev);
                      if (next.has(cat)) next.delete(cat);
                      else next.add(cat);
                      return next;
                    });
                  };
                  
                  return Object.entries(grouped).map(([category, items]) => {
                    if (items.length === 0) return null;
                    
                    const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS['Other Expenses'];
                    const isExpanded = consolidatedExpandedCategories.has(category);
                    const categoryTotal = items.reduce((sum, item) => sum + item.rowTotal, 0);
                    
                    return (
                      <Card key={category} className={`border shadow-none rounded-sm overflow-hidden ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                        <Collapsible open={isExpanded} onOpenChange={() => toggleCategory(category)}>
                          <CollapsibleTrigger asChild>
                            <CardHeader className={`py-3 px-4 ${colors.bg} ${colors.border} border-b cursor-pointer hover:opacity-90`}>
                              <div className="flex items-center justify-between">
                                <CardTitle className={`font-display text-base font-medium flex items-center gap-2 ${colors.text}`}>
                                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  <Layers className="w-4 h-4" />
                                  {category}
                                  <Badge variant="outline" className={`ml-2 text-xs ${colors.text}`}>
                                    {items.length} accounts
                                  </Badge>
                                </CardTitle>
                                <div className="font-mono text-sm">
                                  <span className={`text-xs mr-2 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Total:</span>
                                  <span className={colors.text}>{formatCurrency(categoryTotal)}</span>
                                </div>
                              </div>
                            </CardHeader>
                          </CollapsibleTrigger>
                          
                          <CollapsibleContent>
                            <CardContent className="p-0 overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                                    <th className="text-left py-2 px-4 font-medium w-16">Code</th>
                                    <th className="text-left py-2 px-4 font-medium min-w-[200px]">Account</th>
                                    {MONTHS.map(month => (
                                      <th key={month} className="text-right py-2 px-2 font-medium border-l border-slate-200 dark:border-zinc-700 min-w-[80px]">
                                        {MONTH_NAMES[month].substring(0, 3)}
                                      </th>
                                    ))}
                                    <th className="text-right py-2 px-3 font-medium border-l border-slate-200 dark:border-zinc-700 min-w-[100px]">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((item, idx) => (
                                    <tr key={idx} className={`border-t ${isDark ? 'border-zinc-800 hover:bg-zinc-800/30' : 'border-slate-100 hover:bg-slate-50'}`}>
                                      <td className={`py-2 px-4 font-mono text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                        {item.account_code}
                                      </td>
                                      <td className={`py-2 px-4 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                        {item.account_name}
                                      </td>
                                      {MONTHS.map(month => (
                                        <td key={month} className={`py-2 px-2 text-right font-mono text-xs border-l ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-slate-200 text-slate-700'}`}>
                                          {(item[month] || 0) !== 0 ? formatCurrency(item[month] || 0) : '-'}
                                        </td>
                                      ))}
                                      <td className={`py-2 px-3 text-right font-mono font-medium border-l ${isDark ? 'border-zinc-700 text-white' : 'border-slate-200 text-slate-900'}`}>
                                        {formatCurrency(item.rowTotal)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </CardContent>
                          </CollapsibleContent>
                        </Collapsible>
                      </Card>
                    );
                  });
                })()}
              </div>

              {/* Financial Summary - Bottom of Consolidated View */}
              <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                  <CardTitle className="font-display text-base font-medium flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-emerald-600" />
                    Financial Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {(() => {
                    // Calculate totals from consolidated data
                    const totals = {
                      sales: 0, purchases: 0, overhead: 0, salaries: 0, mgmtFees: 0, otherExpenses: 0,
                    };
                    
                    for (const item of consolidatedData.line_items) {
                      const rowTotal = MONTHS.reduce((sum, m) => sum + (item[m] || 0), 0);
                      const category = item.category || 'Other Expenses';
                      if (category === 'Sales') totals.sales += rowTotal;
                      else if (category === 'Purchases') totals.purchases += rowTotal;
                      else if (category === 'Overhead') totals.overhead += rowTotal;
                      else if (category === 'Salaries') totals.salaries += rowTotal;
                      else if (category === 'Management Fees') totals.mgmtFees += rowTotal;
                      else totals.otherExpenses += rowTotal;
                    }
                    
                    const grossProfit = totals.sales - totals.purchases;
                    const opex = totals.overhead + totals.salaries + totals.mgmtFees + totals.otherExpenses;
                    // OPEX is already negative (sum of negative expenses), so we ADD it
                    const operatingProfit = grossProfit + opex;
                    const netProfit = operatingProfit;
                    
                    return (
                      <div className={`overflow-x-auto`}>
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`py-2 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Sales</td>
                              <td className={`py-2 text-right font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatCurrency(totals.sales)}</td>
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`py-2 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Purchases</td>
                              <td className={`py-2 text-right font-mono text-red-600`}>{formatCurrencyAsExpense(totals.purchases)}</td>
                            </tr>
                            <tr className={`border-b-2 ${isDark ? 'border-zinc-700 bg-emerald-900/10' : 'border-slate-200 bg-emerald-50'}`}>
                              <td className={`py-2 font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Gross Profit</td>
                              <td className={`py-2 text-right font-mono font-semibold ${grossProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(grossProfit)}</td>
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`py-2 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Overhead</td>
                              <td className={`py-2 text-right font-mono text-red-600`}>{formatCurrencyAsExpense(totals.overhead)}</td>
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`py-2 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Salaries</td>
                              <td className={`py-2 text-right font-mono text-red-600`}>{formatCurrencyAsExpense(totals.salaries)}</td>
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`py-2 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Management Fees</td>
                              <td className={`py-2 text-right font-mono text-red-600`}>{formatCurrencyAsExpense(totals.mgmtFees)}</td>
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`py-2 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Less: Other Expenses</td>
                              <td className={`py-2 text-right font-mono text-red-600`}>{formatCurrencyAsExpense(totals.otherExpenses)}</td>
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                              <td className={`py-2 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Total Opex</td>
                              <td className={`py-2 text-right font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatCurrencyAsExpense(opex)}</td>
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                              <td className={`py-2 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Operating Profit</td>
                              <td className={`py-2 text-right font-mono font-medium ${operatingProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(operatingProfit)}</td>
                            </tr>
                            <tr className={`${isDark ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
                              <td className={`py-3 font-bold text-lg ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Net Profit / (Loss)</td>
                              <td className={`py-3 text-right font-mono font-bold text-lg ${netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(netProfit)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <CardContent className={`p-8 text-center ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                <LayoutGrid className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
                <p>No consolidated data available</p>
                <p className="text-sm mt-1">Select a fiscal year and ensure there are approved budgets</p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
            <CardTitle className="font-display text-lg font-medium">
              Budgets ({budgets.length})
            </CardTitle>
          </CardHeader>
          <CardContent className={viewStyle === 'tile' ? 'p-4' : 'p-0'}>
            {budgets.length > 0 ? (
              viewStyle === 'list' ? (
                /* LIST VIEW */
                <div className={`divide-y ${isDark ? 'divide-zinc-800' : 'divide-slate-100'}`}>
                  {budgets.map((budget) => {
                    const statusConfig = BUDGET_STATUS[budget.status] || BUDGET_STATUS.draft;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <div 
                        key={budget.id} 
                        className={`p-4 flex items-center justify-between cursor-pointer ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-50'}`}
                        onClick={() => openBudgetDetail(budget)}
                        data-testid={`budget-${budget.id}`}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                            isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'
                          }`}>
                            <Calculator className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                {budget.name}
                              </p>
                              <span className={`px-2 py-0.5 rounded-sm text-xs flex items-center gap-1 ${
                                isDark ? statusConfig.darkColor : statusConfig.color
                              }`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig.label}
                              </span>
                              {budget.is_revised && (
                                <Badge variant="outline" className="text-xs">Revised</Badge>
                              )}
                            </div>
                            <div className={`flex items-center gap-4 text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {budget.business_unit_name || 'All BUs'}
                              </span>
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-3 h-3" />
                                FY {budget.fiscal_year}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(budget.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {/* Import Actuals - only for approved budgets */}
                          {budget.status === 'approved' && hasPermission('write') && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setImportActualsModal({ open: true, budget }); 
                              }}
                              className={`rounded-sm text-xs ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                              data-testid={`import-actuals-btn-${budget.id}`}
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              Import Actuals
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={(e) => { e.stopPropagation(); openBudgetDetail(budget); }}
                            className="rounded-sm"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {/* Admin can delete any budget, others can only delete drafts */}
                          {(isAdmin || (hasPermission('delete') && budget.status === 'draft')) && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={(e) => { e.stopPropagation(); handleDeleteBudget(budget.id); }}
                              className="rounded-sm text-red-600"
                              title={isAdmin ? "Delete budget (Admin)" : "Delete draft"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* TILE VIEW */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {budgets.map((budget) => {
                    const statusConfig = BUDGET_STATUS[budget.status] || BUDGET_STATUS.draft;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <Card 
                        key={budget.id}
                        className={`border shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md ${isDark ? 'bg-zinc-800 border-zinc-700 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-400'}`}
                        onClick={() => openBudgetDetail(budget)}
                        data-testid={`budget-tile-${budget.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                              isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'
                            }`}>
                              <Calculator className="w-5 h-5 text-emerald-600" />
                            </div>
                            <span className={`px-2 py-0.5 rounded-sm text-xs flex items-center gap-1 ${
                              isDark ? statusConfig.darkColor : statusConfig.color
                            }`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                          </div>
                          <p className={`font-medium mb-1 truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {budget.name}
                          </p>
                          <div className={`text-xs space-y-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            <p className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {budget.business_unit_name || 'All BUs'}
                            </p>
                            <p className="flex items-center gap-1">
                              <CalendarIcon className="w-3 h-3" />
                              FY {budget.fiscal_year}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-dashed" style={{ borderColor: isDark ? '#3f3f46' : '#e2e8f0' }}>
                            {budget.status === 'approved' && hasPermission('write') && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setImportActualsModal({ open: true, budget }); 
                                }}
                                className={`rounded-sm text-xs flex-1 ${isDark ? 'bg-zinc-700 border-zinc-600' : ''}`}
                              >
                                <Upload className="w-3 h-3 mr-1" />
                                Import
                              </Button>
                            )}
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); openBudgetDetail(budget); }}
                              className={`rounded-sm text-xs ${budget.status !== 'approved' || !hasPermission('write') ? 'flex-1' : ''} ${isDark ? 'bg-zinc-700 border-zinc-600' : ''}`}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )
            ) : (
              <div className={`p-8 text-center ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                <Calculator className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
                <p>No budgets found</p>
                <p className="text-sm mt-1">Try adjusting your filters or create a new budget</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialog.open} onOpenChange={(open) => !open && setApprovalDialog({ open: false, action: null, budget: null })}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {approvalDialog.action === 'approve' ? (
                <><CheckCircle2 className="w-5 h-5 text-emerald-600" />Approve Budget</>
              ) : (
                <><XCircle className="w-5 h-5 text-red-600" />Reject Budget</>
              )}
            </DialogTitle>
            <DialogDescription>
              {approvalDialog.action === 'approve' 
                ? 'Add an optional comment for this approval.'
                : 'Please provide a reason for rejecting this budget.'}
            </DialogDescription>
          </DialogHeader>
          <ApprovalForm 
            action={approvalDialog.action}
            budget={approvalDialog.budget}
            onSubmit={handleApprovalAction}
            onCancel={() => setApprovalDialog({ open: false, action: null, budget: null })}
            isDark={isDark}
          />
        </DialogContent>
      </Dialog>

      {/* Revision Dialog */}
      <Dialog open={revisionDialog.open} onOpenChange={(open) => !open && setRevisionDialog({ open: false, budget: null })}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-600" />
              Revise Budget
            </DialogTitle>
            <DialogDescription>
              Mark this budget for revision. The original figures will be preserved for comparison.
            </DialogDescription>
          </DialogHeader>
          <RevisionForm 
            budget={revisionDialog.budget}
            onSubmit={handleRevision}
            onCancel={() => setRevisionDialog({ open: false, budget: null })}
            isDark={isDark}
          />
        </DialogContent>
      </Dialog>

      {/* Create Budget Dialog */}
      <Dialog open={createBudgetDialog} onOpenChange={(open) => !creatingBudget && setCreateBudgetDialog(open)}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-600" />
              Create New Budget
            </DialogTitle>
            <DialogDescription>
              Create a new budget using the account template. You can manually enter figures after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Budget Name *</Label>
              <Input
                value={newBudgetName}
                onChange={(e) => setNewBudgetName(e.target.value)}
                placeholder="e.g., FY 2025/2026 Annual Budget"
                className="rounded-sm"
                disabled={creatingBudget}
              />
            </div>

            <div className="space-y-2">
              <Label>Business Unit *</Label>
              <Select value={newBudgetBuId} onValueChange={setNewBudgetBuId} disabled={creatingBudget}>
                <SelectTrigger className="rounded-sm">
                  <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Select Business Unit" />
                </SelectTrigger>
                <SelectContent>
                  {sortedBusinessUnits.map(bu => (
                    <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fiscal Year *</Label>
              <Select value={newBudgetFiscalYear} onValueChange={setNewBudgetFiscalYear} disabled={creatingBudget}>
                <SelectTrigger className="rounded-sm">
                  <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Select Fiscal Year" />
                </SelectTrigger>
                <SelectContent>
                  {sortFiscalYearsNewestFirst(fiscalYearOptions).map(fy => (
                    <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Selection */}
            <div className="space-y-3">
              <Label>Create From</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setBudgetSource('scratch');
                    setSelectedForecastId('');
                  }}
                  className={`p-3 rounded-sm border-2 text-left transition-all ${
                    budgetSource === 'scratch'
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <FileText className={`w-5 h-5 mb-1 ${budgetSource === 'scratch' ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <p className={`text-sm font-medium ${budgetSource === 'scratch' ? 'text-emerald-700 dark:text-emerald-400' : ''}`}>
                    Start from Scratch
                  </p>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                    Empty template
                  </p>
                </button>
                
                <button
                  type="button"
                  onClick={() => setBudgetSource('forecast')}
                  disabled={availableForecasts.length === 0}
                  className={`p-3 rounded-sm border-2 text-left transition-all ${
                    budgetSource === 'forecast'
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                      : availableForecasts.length === 0
                        ? 'opacity-50 cursor-not-allowed border-slate-200 dark:border-zinc-700'
                        : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <TrendingUp className={`w-5 h-5 mb-1 ${budgetSource === 'forecast' ? 'text-violet-600' : 'text-slate-400'}`} />
                  <p className={`text-sm font-medium ${budgetSource === 'forecast' ? 'text-violet-700 dark:text-violet-400' : ''}`}>
                    From Forecast
                  </p>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                    {availableForecasts.length > 0 ? `${availableForecasts.length} available` : 'No forecasts'}
                  </p>
                </button>
              </div>
              
              {/* Forecast Selection (when "From Forecast" is selected) */}
              {budgetSource === 'forecast' && availableForecasts.length > 0 && (
                <div className="space-y-2 mt-2">
                  <Label>Select Forecast *</Label>
                  <Select value={selectedForecastId} onValueChange={setSelectedForecastId} disabled={creatingBudget}>
                    <SelectTrigger className="rounded-sm">
                      <TrendingUp className="w-4 h-4 mr-2 text-violet-500" />
                      <SelectValue placeholder="Select a forecast" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableForecasts.map(forecast => (
                        <SelectItem key={forecast.id} value={forecast.id}>
                          <div className="flex items-center gap-2">
                            <span>{forecast.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              forecast.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {forecast.status}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className={`p-3 rounded-sm text-sm ${isDark ? 'bg-emerald-900/20 text-emerald-400' : 'bg-emerald-50 text-emerald-700'}`}>
              <strong>Note:</strong> {budgetSource === 'forecast' 
                ? 'Budget will be created from the selected forecast with all line items copied.'
                : 'Budget will be created as Draft using the Chart of Accounts template.'
              } You can edit figures directly and submit for approval when ready.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setCreateBudgetDialog(false);
                setBudgetSource('scratch');
                setSelectedForecastId('');
              }} 
              className="rounded-sm"
              disabled={creatingBudget}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateBudget}
              disabled={creatingBudget}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
            >
              {creatingBudget ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Budget
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Actuals Modal */}
      <ImportActualsModal
        isOpen={importActualsModal.open}
        onClose={() => setImportActualsModal({ open: false, budget: null })}
        budget={importActualsModal.budget}
        onImportSuccess={() => {
          loadBudgets();
          toast.success('Actuals imported. View Budget vs Actuals to see the updated data.');
        }}
      />
    </div>
  );
};

// Budget Detail View Component with Nested Collapsible Sections and Editing
const BudgetDetailView = ({ 
  budget, currentBudget, displayMonths, timePeriod, setTimePeriod,
  canApprove, hasWritePermission, isViewingPrevious,
  availablePreviousBudgets, selectedPreviousBudgetId, onSelectPreviousBudget,
  onBack, onApprove, onReject, onSubmitForApproval, onRevise, onSaveChanges,
  isDark 
}) => {
  const statusConfig = BUDGET_STATUS[budget.status] || BUDGET_STATUS.draft;
  const StatusIcon = statusConfig.icon;
  
  // Expanded state for categories and main accounts
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses']));
  const [expandedMainAccounts, setExpandedMainAccounts] = useState(new Set());
  
  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editedLineItems, setEditedLineItems] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Comments panel state
  const [commentsPanelOpen, setCommentsPanelOpen] = useState(false);
  const [selectedAccountForComments, setSelectedAccountForComments] = useState({ code: '', name: '' });
  
  const openCommentsPanel = (accountCode, accountName) => {
    setSelectedAccountForComments({ code: accountCode, name: accountName });
    setCommentsPanelOpen(true);
  };
  
  // Can edit if: draft, pending, or revised (and not viewing previous)
  const canEdit = !isViewingPrevious && hasWritePermission && 
    (budget.status === 'draft' || budget.status === 'pending' || budget.is_revised);

  // Initialize edited line items when budget changes
  useEffect(() => {
    if (budget?.line_items) {
      setEditedLineItems(JSON.parse(JSON.stringify(budget.line_items)));
    }
  }, [budget?.id]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const toggleMainAccount = (accountCode) => {
    setExpandedMainAccounts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountCode)) {
        newSet.delete(accountCode);
      } else {
        newSet.add(accountCode);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedCategories(new Set(['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses']));
    const allMainAccounts = budget.line_items?.filter(i => i.is_header).map(i => i.account_code) || [];
    setExpandedMainAccounts(new Set(allMainAccounts));
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setExpandedMainAccounts(new Set());
  };

  const handleValueChange = (itemIndex, month, value) => {
    const numValue = parseFloat(value) || 0;
    setEditedLineItems(prev => {
      const updated = [...prev];
      updated[itemIndex] = { ...updated[itemIndex], [month]: numValue };
      return updated;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveChanges(editedLineItems);
      setHasChanges(false);
      setIsEditing(false);
      toast.success('Budget saved successfully');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedLineItems(JSON.parse(JSON.stringify(budget.line_items)));
    setHasChanges(false);
    setIsEditing(false);
  };

  // Use edited line items when editing, otherwise use budget line items
  const lineItemsToDisplay = isEditing ? editedLineItems : (budget.line_items || []);

  // Group line items by category, then by main account
  const groupedItems = useMemo(() => {
    const groups = {};
    const categoryOrder = ['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses'];
    
    categoryOrder.forEach(cat => {
      groups[cat] = { mainAccounts: {} };
    });
    
    lineItemsToDisplay.forEach((item, index) => {
      const category = item.category;
      if (!category || !groups[category]) return;
      
      // Add index for editing reference
      const itemWithIndex = { ...item, _index: index };
      
      if (item.is_header) {
        // This is a main account header
        if (!groups[category].mainAccounts[item.account_code]) {
          groups[category].mainAccounts[item.account_code] = {
            header: itemWithIndex,
            subAccounts: []
          };
        } else {
          groups[category].mainAccounts[item.account_code].header = itemWithIndex;
        }
      } else {
        // This is a sub-account
        const parentCode = item.parent_code || 'ungrouped';
        if (!groups[category].mainAccounts[parentCode]) {
          groups[category].mainAccounts[parentCode] = {
            header: null,
            subAccounts: []
          };
        }
        groups[category].mainAccounts[parentCode].subAccounts.push(itemWithIndex);
      }
    });
    
    return groups;
  }, [lineItemsToDisplay]);

  // Calculate totals
  const calculateItemTotal = (item) => {
    return displayMonths.reduce((sum, month) => sum + (item[month] || 0), 0);
  };

  const calculateMainAccountTotal = (mainAccountData, month) => {
    return mainAccountData.subAccounts.reduce((sum, item) => sum + (item[month] || 0), 0);
  };

  const calculateCategoryTotal = (category, month, type = 'budget') => {
    const mainAccounts = groupedItems[category]?.mainAccounts || {};
    let total = 0;
    Object.values(mainAccounts).forEach(ma => {
      ma.subAccounts.forEach(item => {
        if (type === 'actual') {
          total += (item[`${month}_actual`] || 0);
        } else {
          total += (item[month] || 0);
        }
      });
    });
    return total;
  };

  const calculateCategoryGrandTotal = (category, type = 'budget') => {
    return displayMonths.reduce((sum, month) => sum + calculateCategoryTotal(category, month, type), 0);
  };

  // Check if budget has actual data
  const hasActualData = useMemo(() => {
    const items = budget?.line_items || [];
    return items.some(item => 
      MONTHS.some(m => (item[`${m}_actual`] || 0) !== 0)
    );
  }, [budget]);

  // Calculate summary totals with both budget and actual
  const totals = useMemo(() => {
    const salesBudget = calculateCategoryGrandTotal('Sales', 'budget');
    const salesActual = calculateCategoryGrandTotal('Sales', 'actual');
    const purchasesBudget = calculateCategoryGrandTotal('Purchases', 'budget');
    const purchasesActual = calculateCategoryGrandTotal('Purchases', 'actual');
    const overheadBudget = calculateCategoryGrandTotal('Overhead', 'budget');
    const overheadActual = calculateCategoryGrandTotal('Overhead', 'actual');
    const salariesBudget = calculateCategoryGrandTotal('Salaries', 'budget');
    const salariesActual = calculateCategoryGrandTotal('Salaries', 'actual');
    const managementFeesBudget = calculateCategoryGrandTotal('Management Fees', 'budget');
    const managementFeesActual = calculateCategoryGrandTotal('Management Fees', 'actual');
    const otherExpensesBudget = calculateCategoryGrandTotal('Other Expenses', 'budget');
    const otherExpensesActual = calculateCategoryGrandTotal('Other Expenses', 'actual');
    
    const grossProfitBudget = salesBudget - purchasesBudget;
    const grossProfitActual = salesActual - purchasesActual;
    const opexBudget = overheadBudget + salariesBudget;
    const opexActual = overheadActual + salariesActual;
    const operatingProfitBudget = grossProfitBudget + opexBudget + managementFeesBudget;
    const operatingProfitActual = grossProfitActual + opexActual + managementFeesActual;
    const netProfitBudget = operatingProfitBudget + otherExpensesBudget;
    const netProfitActual = operatingProfitActual + otherExpensesActual;
    
    return {
      sales: salesBudget,
      salesActual,
      purchases: purchasesBudget,
      purchasesActual,
      overhead: overheadBudget,
      overheadActual,
      salaries: salariesBudget,
      salariesActual,
      managementFees: managementFeesBudget,
      managementFeesActual,
      otherExpenses: otherExpensesBudget,
      otherExpensesActual,
      grossProfit: grossProfitBudget,
      grossProfitActual,
      opex: opexBudget,
      opexActual,
      operatingProfit: operatingProfitBudget,
      operatingProfitActual,
      netProfit: netProfitBudget,
      netProfitActual,
      // Percentages (of sales budget)
      grossProfitPct: salesBudget > 0 ? (grossProfitBudget / salesBudget * 100) : 0,
      opexPct: salesBudget > 0 ? (opexBudget / salesBudget * 100) : 0,
      operatingProfitPct: salesBudget > 0 ? (operatingProfitBudget / salesBudget * 100) : 0,
      netProfitPct: salesBudget > 0 ? (netProfitBudget / salesBudget * 100) : 0,
    };
  }, [groupedItems, displayMonths]);

  return (
    <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="budget-detail-view">
      {/* Safety check for missing budget data */}
      {!budget || !budget.name ? (
        <div className="text-center py-12">
          <p className={isDark ? 'text-zinc-400' : 'text-slate-500'}>Loading budget data...</p>
        </div>
      ) : (
      <>
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-sm mt-1" data-testid="back-to-budgets">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className={`font-display text-2xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {budget.name}
            </h1>
            <span className={`px-3 py-1 rounded-sm text-sm flex items-center gap-1.5 ${
              isDark ? statusConfig.darkColor : statusConfig.color
            }`}>
              <StatusIcon className="w-4 h-4" />
              {statusConfig.label}
            </span>
            {budget.is_revised && <Badge variant="outline">Revised</Badge>}
            {isViewingPrevious && (
              <Badge variant="outline" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                <History className="w-3 h-3 mr-1" />
                Viewing Previous Budget
              </Badge>
            )}
          </div>
          <p className={isDark ? 'text-zinc-400 mt-1' : 'text-slate-500 mt-1'}>
            FY {budget.fiscal_year} &bull; {budget.business_unit_name}
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Previous Budget Selector */}
          {availablePreviousBudgets.length > 0 && (
            <Select 
              value={selectedPreviousBudgetId || 'current'} 
              onValueChange={(v) => onSelectPreviousBudget(v === 'current' ? null : v)}
            >
              <SelectTrigger className={`w-[200px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                <History className="w-4 h-4 mr-2" />
                <SelectValue placeholder="View previous budget" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="current">Current Budget</SelectItem>
                {availablePreviousBudgets.map(pb => (
                  <SelectItem key={pb.id} value={pb.id}>
                    {pb.name} ({pb.fiscal_year})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Time Period Filter */}
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className={`w-[150px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map(period => (
                <SelectItem key={period.value} value={period.value}>{period.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Approval actions - only show for current budget, not previous */}
          {!isViewingPrevious && (
            <>
              {/* Edit button for editable budgets */}
              {canEdit && !isEditing && (
                <Button onClick={() => setIsEditing(true)} variant="outline" className="rounded-sm">
                  <Edit className="w-4 h-4 mr-2" />Edit Figures
                </Button>
              )}
              
              {/* Save/Cancel when editing */}
              {isEditing && (
                <>
                  <Button 
                    onClick={handleCancelEdit} 
                    variant="outline" 
                    className="rounded-sm"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSave} 
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                    disabled={saving || !hasChanges}
                  >
                    {saving ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                      <><CheckCircle2 className="w-4 h-4 mr-2" />Save Changes</>
                    )}
                  </Button>
                </>
              )}
              
              {currentBudget.status === 'draft' && hasWritePermission && !isEditing && (
                <Button onClick={onSubmitForApproval} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm">
                  <Send className="w-4 h-4 mr-2" />Submit for Approval
                </Button>
              )}
              {currentBudget.status === 'pending' && canApprove && !isEditing && (
                <>
                  <Button onClick={onApprove} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
                    <CheckCircle2 className="w-4 h-4 mr-2" />Approve
                  </Button>
                  <Button variant="outline" onClick={onReject} className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm">
                    <XCircle className="w-4 h-4 mr-2" />Reject
                  </Button>
                </>
              )}
              {currentBudget.status === 'approved' && hasWritePermission && !currentBudget.is_revised && !isEditing && (
                <Button variant="outline" onClick={onRevise} className="rounded-sm">
                  <RefreshCw className="w-4 h-4 mr-2" />Revise Budget
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Editing Mode Banner */}
      {isEditing && (
        <div className={`p-3 rounded-sm flex items-center justify-between ${isDark ? 'bg-amber-900/30 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center gap-2">
            <Edit className="w-4 h-4 text-amber-600" />
            <span className={isDark ? 'text-amber-400' : 'text-amber-700'}>
              <strong>Edit Mode:</strong> Click on any figure to edit. Calculated fields (totals, margins) update automatically.
            </span>
          </div>
          {hasChanges && (
            <Badge className="bg-amber-100 text-amber-700">Unsaved Changes</Badge>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <SummaryCard label="Sales" value={totals.sales} isDark={isDark} color="emerald" />
        <SummaryCard label="Purchases" value={totals.purchases} isDark={isDark} color="red" isNegative />
        <SummaryCard label="Gross Profit" value={totals.grossProfit} pct={totals.grossProfitPct} isDark={isDark} color="emerald" />
        <SummaryCard label="Overhead" value={totals.overhead} isDark={isDark} color="amber" isNegative />
        <SummaryCard label="Salaries" value={totals.salaries} isDark={isDark} color="blue" isNegative />
        <SummaryCard label="Operating Profit" value={totals.operatingProfit} pct={totals.operatingProfitPct} isDark={isDark} color={totals.operatingProfit >= 0 ? 'emerald' : 'red'} />
        <SummaryCard label="Other Expenses" value={totals.otherExpenses} isDark={isDark} color="slate" isNegative />
        <SummaryCard label="Net Profit" value={totals.netProfit} pct={totals.netProfitPct} isDark={isDark} color={totals.netProfit >= 0 ? 'emerald' : 'red'} highlight />
      </div>

      {/* Expand/Collapse Controls */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={expandAll} className="rounded-sm text-xs">
          Expand All
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAll} className="rounded-sm text-xs">
          Collapse All
        </Button>
      </div>

      {/* Budget Table by Category with Nested Collapsible */}
      <div className="space-y-4">
        {Object.entries(groupedItems).map(([category, data]) => {
          const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS['Other Expenses'];
          const categoryTotal = calculateCategoryGrandTotal(category);
          const isExpanded = expandedCategories.has(category);
          const mainAccounts = Object.entries(data.mainAccounts).filter(([_, ma]) => ma.subAccounts.length > 0 || ma.header);
          
          if (mainAccounts.length === 0) return null;
          
          return (
            <Card key={category} className={`border shadow-none rounded-sm overflow-hidden ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              {/* Category Header - Collapsible */}
              <Collapsible open={isExpanded} onOpenChange={() => toggleCategory(category)}>
                <CollapsibleTrigger asChild>
                  <CardHeader className={`py-3 px-4 ${colors.bg} ${colors.border} border-b cursor-pointer hover:opacity-90`}>
                    <div className="flex items-center justify-between">
                      <CardTitle className={`font-display text-base font-medium flex items-center gap-2 ${colors.text}`}>
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        <Layers className="w-4 h-4" />
                        {category}
                        <span className={`text-xs font-normal ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                          ({mainAccounts.length} accounts)
                        </span>
                      </CardTitle>
                      <span className={`font-mono font-medium ${colors.text}`}>
                        {formatCurrency(categoryTotal)}
                      </span>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                          <th className="text-left py-2 px-4 font-medium w-16">Code</th>
                          <th className="text-left py-2 px-4 font-medium min-w-[250px]">Account</th>
                          {displayMonths.map(month => (
                            <th key={month} className="text-right py-2 px-3 font-medium w-24">
                              {MONTH_NAMES[month].substring(0, 3)}
                            </th>
                          ))}
                          <th className="text-right py-2 px-4 font-medium w-28">Total</th>
                          <th className="text-center py-2 px-2 font-medium w-12">
                            <MessageSquare className="w-4 h-4 mx-auto text-slate-400" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {mainAccounts.map(([accountCode, mainAccount]) => {
                          const hasSubAccounts = mainAccount.subAccounts.length > 0;
                          const isMainExpanded = expandedMainAccounts.has(accountCode);
                          const mainAccountTotal = displayMonths.reduce((sum, month) => sum + calculateMainAccountTotal(mainAccount, month), 0);
                          
                          return (
                            <Collapsible key={accountCode} open={isMainExpanded} onOpenChange={() => toggleMainAccount(accountCode)} asChild>
                              <>
                                {/* Main Account Header Row */}
                                {mainAccount.header && (
                                  <CollapsibleTrigger asChild>
                                    <tr className={`border-t cursor-pointer ${isDark ? 'border-zinc-700 bg-zinc-800/30 hover:bg-zinc-800/50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}`}>
                                      <td className={`py-2 px-4 font-mono text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                        <div className="flex items-center gap-1">
                                          {hasSubAccounts && (isMainExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />)}
                                          {mainAccount.header.account_code}
                                        </div>
                                      </td>
                                      <td className={`py-2 px-4 font-medium ${colors.text}`}>
                                        {mainAccount.header.account_name}
                                      </td>
                                      {displayMonths.map(month => (
                                        <td key={month} className={`py-2 px-3 text-right font-mono font-medium ${colors.text}`}>
                                          {calculateMainAccountTotal(mainAccount, month) !== 0 ? formatCurrency(calculateMainAccountTotal(mainAccount, month)) : '-'}
                                        </td>
                                      ))}
                                      <td className={`py-2 px-4 text-right font-mono font-bold ${colors.text}`}>
                                        {formatCurrency(mainAccountTotal)}
                                      </td>
                                    </tr>
                                  </CollapsibleTrigger>
                                )}
                                
                                {/* Sub-Account Rows */}
                                <CollapsibleContent asChild>
                                  <>
                                    {mainAccount.subAccounts.map((item, idx) => (
                                      <tr key={idx} className={`border-t ${isDark ? 'border-zinc-800 hover:bg-zinc-800/30' : 'border-slate-100 hover:bg-slate-50'}`}>
                                        <td className={`py-2 px-4 pl-8 font-mono text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                          {item.account_code}
                                        </td>
                                        <td className={`py-2 px-4 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                          {item.account_name}
                                        </td>
                                        {displayMonths.map(month => (
                                          <td key={month} className={`py-1 px-1 text-right`}>
                                            {isEditing ? (
                                              <input
                                                type="number"
                                                value={item[month] || 0}
                                                onChange={(e) => handleValueChange(item._index, month, e.target.value)}
                                                className={`w-full px-2 py-1 text-right font-mono text-sm rounded border ${
                                                  isDark 
                                                    ? 'bg-zinc-800 border-zinc-600 text-white focus:border-emerald-500' 
                                                    : 'bg-white border-slate-300 text-slate-900 focus:border-emerald-500'
                                                } focus:outline-none focus:ring-1 focus:ring-emerald-500`}
                                                style={{ width: '100px' }}
                                              />
                                            ) : (
                                              <span className={`font-mono ${
                                                item[month] !== 0 
                                                  ? category === 'Sales' && item[month] > 0 ? 'text-emerald-600' : isDark ? 'text-zinc-300' : 'text-slate-700'
                                                  : isDark ? 'text-zinc-600' : 'text-slate-300'
                                              }`}>
                                                {item[month] !== 0 ? formatCurrency(item[month]) : '-'}
                                              </span>
                                            )}
                                          </td>
                                        ))}
                                        <td className={`py-2 px-4 text-right font-mono font-medium ${
                                          category === 'Sales' ? 'text-emerald-600' : isDark ? 'text-white' : 'text-slate-900'
                                        }`}>
                                          {formatCurrency(calculateItemTotal(item))}
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 rounded-sm"
                                            onClick={() => openCommentsPanel(item.account_code, item.account_name)}
                                            data-testid={`budget-comment-btn-${item.account_code}`}
                                          >
                                            <MessageSquare className={`w-4 h-4 ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-slate-400 hover:text-slate-600'}`} />
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </>
                                </CollapsibleContent>
                              </>
                            </Collapsible>
                          );
                        })}
                        
                        {/* Category Total Row */}
                        <tr className={`border-t-2 ${isDark ? 'border-zinc-600 bg-zinc-800' : 'border-slate-300 bg-slate-100'}`}>
                          <td colSpan={2} className={`py-3 px-4 font-bold ${colors.text}`}>
                            Total {category}
                          </td>
                          {displayMonths.map(month => (
                            <td key={month} className={`py-3 px-3 text-right font-mono font-bold ${colors.text}`}>
                              {calculateCategoryTotal(category, month) !== 0 ? formatCurrency(calculateCategoryTotal(category, month)) : '-'}
                            </td>
                          ))}
                          <td className={`py-3 px-4 text-right font-mono font-bold text-lg ${colors.text}`}>
                            {formatCurrency(categoryTotal)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {/* Financial Summary with Monthly Breakdown */}
      <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
        <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
          <CardTitle className="font-display text-lg font-medium">Financial Summary</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                <th className="text-left py-3 px-4 font-medium min-w-[200px]">Description</th>
                {displayMonths.map(month => (
                  <th key={month} className="text-right py-3 px-3 font-medium w-24">
                    {MONTH_NAMES[month].substring(0, 3)}
                  </th>
                ))}
                {hasActualData ? (
                  <>
                    <th className={`text-right py-3 px-3 font-medium w-28 ${isDark ? 'bg-zinc-700' : 'bg-slate-100'}`}>Total Act</th>
                    <th className={`text-right py-3 px-3 font-medium w-28 ${isDark ? 'bg-zinc-700' : 'bg-slate-100'}`}>Total Bud</th>
                    <th className={`text-right py-3 px-3 font-medium w-28 ${isDark ? 'bg-zinc-700' : 'bg-slate-100'}`}>Variance</th>
                    <th className={`text-right py-3 px-3 font-medium w-20 ${isDark ? 'bg-zinc-700' : 'bg-slate-100'}`}>%</th>
                  </>
                ) : (
                  <>
                    <th className="text-right py-3 px-4 font-medium w-32">Total</th>
                    <th className="text-right py-3 px-4 font-medium w-20">%</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              <MonthlySummaryRow 
                label="Total Sales" 
                category="Sales"
                calculateCategoryTotal={calculateCategoryTotal}
                displayMonths={displayMonths}
                total={totals.sales}
                totalActual={totals.salesActual}
                isDark={isDark} 
                isPositive
                showYTDColumns={hasActualData}
              />
              <MonthlySummaryRow 
                label="Less: Cost of Sales" 
                category="Purchases"
                calculateCategoryTotal={calculateCategoryTotal}
                displayMonths={displayMonths}
                total={totals.purchases}
                totalActual={totals.purchasesActual}
                isDark={isDark} 
                isNegative
                showYTDColumns={hasActualData}
              />
              <MonthlySummaryRow 
                label="Gross Profit" 
                calculateCategoryTotal={(cat, month, type) => calculateCategoryTotal('Sales', month, type) - calculateCategoryTotal('Purchases', month, type)}
                displayMonths={displayMonths}
                total={totals.grossProfit}
                totalActual={totals.grossProfitActual}
                pct={totals.grossProfitPct}
                isDark={isDark} 
                isTotal 
                highlight="emerald"
                showYTDColumns={hasActualData}
              />
              <MonthlySummaryRow 
                label="Less: Overhead" 
                category="Overhead"
                calculateCategoryTotal={calculateCategoryTotal}
                displayMonths={displayMonths}
                total={totals.overhead}
                totalActual={totals.overheadActual}
                isDark={isDark} 
                isNegative 
                showYTDColumns={hasActualData}
              />
              <MonthlySummaryRow 
                label="Less: Salaries" 
                category="Salaries"
                calculateCategoryTotal={calculateCategoryTotal}
                displayMonths={displayMonths}
                total={totals.salaries}
                totalActual={totals.salariesActual}
                isDark={isDark} 
                isNegative 
                showYTDColumns={hasActualData}
              />
              <MonthlySummaryRow 
                label="Total Opex" 
                calculateCategoryTotal={(cat, month, type) => calculateCategoryTotal('Overhead', month, type) + calculateCategoryTotal('Salaries', month, type)}
                displayMonths={displayMonths}
                total={totals.opex}
                totalActual={totals.opexActual}
                pct={totals.opexPct}
                isDark={isDark} 
                isTotal 
                showYTDColumns={hasActualData}
              />
              <MonthlySummaryRow 
                label="Less: Management Fees" 
                category="Management Fees"
                calculateCategoryTotal={calculateCategoryTotal}
                displayMonths={displayMonths}
                total={totals.managementFees}
                totalActual={totals.managementFeesActual}
                isDark={isDark} 
                isNegative 
                showYTDColumns={hasActualData}
              />
              <MonthlySummaryRow 
                label="Operating Profit" 
                calculateCategoryTotal={(cat, month, type) => {
                  const sales = calculateCategoryTotal('Sales', month, type);
                  const purchases = calculateCategoryTotal('Purchases', month, type);
                  const overhead = calculateCategoryTotal('Overhead', month, type);
                  const salaries = calculateCategoryTotal('Salaries', month, type);
                  const mgmtFees = calculateCategoryTotal('Management Fees', month, type);
                  return (sales - purchases) + overhead + salaries + mgmtFees;
                }}
                displayMonths={displayMonths}
                total={totals.operatingProfit}
                totalActual={totals.operatingProfitActual}
                pct={totals.operatingProfitPct}
                isDark={isDark} 
                isTotal 
                highlight="blue" 
                showYTDColumns={hasActualData}
              />
              <MonthlySummaryRow 
                label="Less: Other Expenses" 
                category="Other Expenses"
                calculateCategoryTotal={calculateCategoryTotal}
                displayMonths={displayMonths}
                total={totals.otherExpenses}
                totalActual={totals.otherExpensesActual}
                isDark={isDark} 
                isNegative 
                showYTDColumns={hasActualData}
              />
              <MonthlySummaryRow 
                label="Net Profit/(Loss)" 
                calculateCategoryTotal={(cat, month, type) => {
                  const sales = calculateCategoryTotal('Sales', month, type);
                  const purchases = calculateCategoryTotal('Purchases', month, type);
                  const overhead = calculateCategoryTotal('Overhead', month, type);
                  const salaries = calculateCategoryTotal('Salaries', month, type);
                  const mgmtFees = calculateCategoryTotal('Management Fees', month, type);
                  const other = calculateCategoryTotal('Other Expenses', month, type);
                  return (sales - purchases) + overhead + salaries + mgmtFees + other;
                }}
                displayMonths={displayMonths}
                total={totals.netProfit}
                totalActual={totals.netProfitActual}
                pct={totals.netProfitPct}
                isDark={isDark} 
                isTotal 
                highlight={totals.netProfit >= 0 ? 'emerald' : 'red'} 
                isFinal 
                showYTDColumns={hasActualData}
              />
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Comments Side Panel */}
      <CommentsSidePanel
        isOpen={commentsPanelOpen}
        onClose={() => setCommentsPanelOpen(false)}
        budgetId={budget.id}
        accountCode={selectedAccountForComments.code}
        accountName={selectedAccountForComments.name}
        showVarianceComments={false}
      />
      </>
      )}
    </div>
  );
};

// Helper Components
const SummaryCard = ({ label, value, pct, isDark, color, isNegative, highlight }) => (
  <Card className={`border shadow-none rounded-sm ${highlight ? `ring-2 ring-${color}-600` : ''} ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
    <CardContent className="p-3">
      <p className={`text-xs truncate ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>{label}</p>
      <p className={`font-mono text-sm font-medium mt-1 ${
        color === 'emerald' && value >= 0 ? 'text-emerald-600' :
        color === 'red' || value < 0 ? 'text-red-600' :
        color === 'amber' ? 'text-amber-600' :
        color === 'blue' ? 'text-blue-600' :
        isDark ? 'text-white' : 'text-slate-900'
      }`}>
        {formatCurrency(value)}
      </p>
      {pct !== undefined && (
        <p className={`text-xs mt-0.5 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
          {pct.toFixed(1)}%
        </p>
      )}
    </CardContent>
  </Card>
);

const SummaryRow = ({ label, value, pct, isDark, isPositive, isNegative, isTotal, highlight, isFinal }) => (
  <tr className={`border-t ${
    isFinal ? isDark ? 'border-zinc-600 bg-zinc-800' : 'border-slate-300 bg-slate-100' :
    isTotal ? isDark ? 'border-zinc-700 bg-zinc-800/50' : 'border-slate-200 bg-slate-50' :
    isDark ? 'border-zinc-800' : 'border-slate-100'
  }`}>
    <td className={`py-3 px-4 ${isTotal || isFinal ? 'font-medium' : ''} ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
      {label}
    </td>
    <td className={`py-3 px-4 text-right font-mono ${isTotal || isFinal ? 'font-bold' : 'font-medium'} ${
      highlight === 'emerald' ? 'text-emerald-600' :
      highlight === 'red' ? 'text-red-600' :
      highlight === 'blue' ? 'text-blue-600' :
      isPositive ? 'text-emerald-600' :
      value < 0 ? 'text-red-600' :
      isDark ? 'text-white' : 'text-slate-900'
    }`}>
      {formatCurrency(value)}
    </td>
    <td className={`py-3 px-4 text-right font-mono w-20 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
      {pct !== undefined ? `${pct.toFixed(1)}%` : ''}
    </td>
  </tr>
);

const MonthlySummaryRow = ({ label, category, calculateCategoryTotal, displayMonths, total, totalActual, pct, isDark, isPositive, isNegative, isTotal, highlight, isFinal, showYTDColumns }) => {
  const getMonthValue = (month, type = 'budget') => {
    if (category) {
      return calculateCategoryTotal(category, month, type);
    }
    return calculateCategoryTotal(null, month, type);
  };

  const variance = (totalActual || 0) - (total || 0);
  const pctOfBudget = total !== 0 ? ((totalActual || 0) / total * 100) : 0;

  return (
    <tr className={`border-t ${
      isFinal ? isDark ? 'border-zinc-600 bg-zinc-800' : 'border-slate-300 bg-slate-100' :
      isTotal ? isDark ? 'border-zinc-700 bg-zinc-800/50' : 'border-slate-200 bg-slate-50' :
      isDark ? 'border-zinc-800' : 'border-slate-100'
    }`}>
      <td className={`py-3 px-4 ${isTotal || isFinal ? 'font-medium' : ''} ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
        {label}
      </td>
      {displayMonths.map(month => {
        const value = getMonthValue(month, 'budget');
        return (
          <td key={month} className={`py-3 px-3 text-right font-mono text-xs ${
            highlight === 'emerald' ? 'text-emerald-600' :
            highlight === 'red' ? 'text-red-600' :
            highlight === 'blue' ? 'text-blue-600' :
            isPositive ? 'text-emerald-600' :
            value < 0 ? 'text-red-600' :
            isDark ? 'text-zinc-300' : 'text-slate-700'
          }`}>
            {value !== 0 ? formatCurrency(value) : '-'}
          </td>
        );
      })}
      
      {/* YTD Columns with Actual/Budget/Variance/% */}
      {showYTDColumns ? (
        <>
          {/* Total Actual */}
          <td className={`py-3 px-3 text-right font-mono ${isTotal || isFinal ? 'font-bold' : 'font-medium'} ${
            highlight === 'emerald' ? 'text-emerald-600' :
            highlight === 'red' ? 'text-red-600' :
            highlight === 'blue' ? 'text-blue-600' :
            (totalActual || 0) < 0 ? 'text-red-600' :
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            {formatCurrency(totalActual || 0)}
          </td>
          {/* Total Budget */}
          <td className={`py-3 px-3 text-right font-mono ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
            {formatCurrency(total || 0)}
          </td>
          {/* Variance */}
          <td className={`py-3 px-3 text-right font-mono ${
            variance >= 0 ? 'text-emerald-600' : 'text-red-600'
          }`}>
            {formatCurrency(variance)}
          </td>
          {/* % of Budget */}
          <td className={`py-3 px-3 text-right font-mono text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
            {total !== 0 ? `${pctOfBudget.toFixed(1)}%` : '-'}
          </td>
        </>
      ) : (
        <>
          {/* Simple Total and % columns */}
          <td className={`py-3 px-4 text-right font-mono ${isTotal || isFinal ? 'font-bold' : 'font-medium'} ${
            highlight === 'emerald' ? 'text-emerald-600' :
            highlight === 'red' ? 'text-red-600' :
            highlight === 'blue' ? 'text-blue-600' :
            isPositive ? 'text-emerald-600' :
            total < 0 ? 'text-red-600' :
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            {formatCurrency(total)}
          </td>
          <td className={`py-3 px-4 text-right font-mono text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
            {pct !== undefined ? `${pct.toFixed(1)}%` : ''}
          </td>
        </>
      )}
    </tr>
  );
};

const ApprovalForm = ({ action, budget, onSubmit, onCancel, isDark }) => {
  const [comment, setComment] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (action === 'reject' && !comment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    onSubmit(comment);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={`p-3 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
        <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Budget</p>
        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{budget?.name}</p>
      </div>
      <div className="space-y-2">
        <Label>{action === 'approve' ? 'Comment (Optional)' : 'Rejection Reason *'}</Label>
        <Textarea 
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={action === 'approve' ? 'Add any notes...' : 'Explain why this budget is being rejected...'}
          className="rounded-sm min-h-[100px]"
          required={action === 'reject'}
        />
      </div>
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
        <Button type="submit" className={`rounded-sm ${action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'} text-white`}>
          {action === 'approve' ? 'Approve Budget' : 'Reject Budget'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const RevisionForm = ({ budget, onSubmit, onCancel, isDark }) => {
  const [note, setNote] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!note.trim()) {
      toast.error('Please provide a revision note');
      return;
    }
    onSubmit(note);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={`p-3 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
        <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Budget</p>
        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{budget?.name}</p>
      </div>
      <div className={`p-3 rounded-sm ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
        <p className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
          <strong>Note:</strong> Original budget figures will be preserved. You can then update the revised figures while still seeing the original budget for comparison.
        </p>
      </div>
      <div className="space-y-2">
        <Label>Revision Note *</Label>
        <Textarea 
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Explain the reason for this budget revision..."
          className="rounded-sm min-h-[100px]"
          required
        />
      </div>
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
        <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm">
          Create Revision
        </Button>
      </DialogFooter>
    </form>
  );
};

export default BudgetsPage;
